import { Link } from "@remix-run/react";
import { useEffect, useState } from "react";
import { ArrowRight, LogIn } from "lucide-react";
import { Card } from "~/components/ui/card";
import { useRegionConfig } from "~/store/regionConfigStore";
import { useNavStore } from "~/store/navStore";
import {
  getRegionUrlPrefixWithCountryCode,
  workspaceSlug,
} from "~/lib/helpers/slugHelper";
import {
  NAV_DROPDOWN_CARD_CLASS,
  NAV_DROPDOWN_ERROR_CARD_CLASS,
  NAV_DROPDOWN_MAIN_PANEL_CLASS,
  NAV_DROPDOWN_PROMO_PANEL_CLASS,
} from "../constant";

type PartnersProps = {
  /** Opens add-listing modal from Header so it survives the nav dropdown unmounting. */
  onOpenAddListing?: () => void;
};

const columnLabelClass =
  "text-xs font-semibold uppercase tracking-[0.08em] text-oh-grey-heavy";

const viewAllClass =
  "flex h-8 items-center gap-1.5 py-2 text-xs font-bold uppercase tracking-[0.08em] text-oh-grey-heavy transition-colors hover:text-oh-primary";

const MY_OFFICE_HUB_URL = "https://officehub.my.salesforce-sites.com";

type PartnerItem = {
  partnerLogo: string;
  name: string;
  sfid: string;
  aboutPartner: string;
  partnerFavicon?: string;
};
function getPartnerInitials(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0])
    .join("")
    .toUpperCase();
}

function PartnerAvatar({
  partner,
  index,
}: {
  partner: PartnerItem;
  index: number;
}) {
  const [imageError, setImageError] = useState(false);
  const showImage = Boolean(partner.partnerFavicon) && !imageError;

  return (
    <div className='mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-gray-200 ring-1 ring-gray-200'>
      {showImage ? (
        <img
          src={`https://ik.imagekit.io/officehub/tr:w-60,f-avif/${partner.partnerFavicon}`}
          alt={partner.name || `Partner ${index + 1}`}
          className='h-full w-full bg-white object-contain p-0.5'
          loading='lazy'
          onError={() => setImageError(true)}
        />
      ) : (
        <span className='text-md font-semibold text-oh-grey-heavy'>
          {getPartnerInitials(partner.name)}
        </span>
      )}
    </div>
  );
}

export default function Partners({ onOpenAddListing }: PartnersProps = {}) {
  const { regionConfig } = useRegionConfig();
  const { partners, partnersLoading, partnersError, fetchPartners } =
    useNavStore();

  useEffect(() => {
    if (regionConfig.country) {
      fetchPartners(regionConfig.country);
    }
  }, [fetchPartners, regionConfig.country]);

  const regionPrefix = getRegionUrlPrefixWithCountryCode(regionConfig?.region);
  const hasPartners = partners.length > 0;

  if (partnersError) {
    return (
      <Card className={NAV_DROPDOWN_ERROR_CARD_CLASS}>
        <div className='text-center text-red-500'>{partnersError}</div>
      </Card>
    );
  }

  if (partnersLoading) {
    return (
      <Card className={NAV_DROPDOWN_CARD_CLASS}>
        <div className={`${NAV_DROPDOWN_MAIN_PANEL_CLASS} w-full p-6 lg:w-[760px] lg:flex-none lg:p-8`}>
          <div className='flex items-center justify-between gap-4'>
            <div className='h-4 w-48 animate-pulse rounded bg-gray-200' />
            <div className='h-4 w-20 animate-pulse rounded bg-gray-200' />
          </div>
          <div className='mt-4 grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2'>
            {[...Array(8)].map((_, i) => (
              <div key={i} className='flex items-start gap-3 py-2'>
                <div className='h-10 w-10 shrink-0 animate-pulse rounded-full bg-gray-200' />
                <div className='min-w-0 flex-1 space-y-2'>
                  <div className='h-4 w-32 animate-pulse rounded bg-gray-200' />
                  <div className='h-3 w-full animate-pulse rounded bg-gray-200' />
                  <div className='h-3 w-4/5 animate-pulse rounded bg-gray-200' />
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className={NAV_DROPDOWN_PROMO_PANEL_CLASS}>
          <div className='h-4 w-28 animate-pulse rounded bg-gray-200' />
          <div className='mt-2 h-6 w-full animate-pulse rounded bg-gray-200' />
          <div className='mt-4 h-44 w-full animate-pulse rounded-lg bg-gray-200' />
          <div className='mt-4 space-y-2'>
            <div className='h-3 w-full animate-pulse rounded bg-gray-200' />
            <div className='h-3 w-4/5 animate-pulse rounded bg-gray-200' />
          </div>
          <div className='mt-5 h-12 w-full animate-pulse rounded-md bg-gray-200' />
        </div>
      </Card>
    );
  }

  const renderPartnerItem = (partner: PartnerItem, index: number) => (
    <Link
      data-spinner='true'
      to={workspaceSlug({ ...partner, id: partner.sfid })}
      prefetch='intent'
      key={partner.sfid}
      className='group flex items-start gap-3 rounded-md p-2 transition-colors hover:bg-gray-100'
    >
      <PartnerAvatar partner={partner} index={index} />
      <div className='min-w-0 space-y-0.5'>
        <span className='text-base font-semibold leading-6 text-black'>
          {partner.name}
        </span>
        <p className='line-clamp-2 text-sm leading-6 text-oh-gray'>
          {partner.aboutPartner}
        </p>
      </div>
    </Link>
  );

  return (
    <Card className={NAV_DROPDOWN_CARD_CLASS}>
      {hasPartners && (
        <div
          className={`${NAV_DROPDOWN_MAIN_PANEL_CLASS} w-full p-6 pb-1 lg:w-[760px] lg:flex-none lg:p-8 lg:pb-2`}
        >
          <div className='flex items-center justify-between gap-4'>
            <span className={columnLabelClass}>Trusted Workspace Providers</span>
            <Link
              data-spinner='true'
              to={`${regionPrefix}/workspaces`}
              prefetch='intent'
              className={viewAllClass}
            >
              View All
              <ArrowRight size={14} />
            </Link>
          </div>

          <div className='mt-4 grid grid-cols-1 gap-x-8 gap-y-1 sm:grid-cols-2'>
            {partners.slice(0, 6).map(renderPartnerItem)}
          </div>

          <div className='mt-auto border-t border-gray-200 pt-4 pb-2'>
            <Link
              to={MY_OFFICE_HUB_URL}
              target='_blank'
              rel='noopener noreferrer'
              className='group flex items-center gap-3 rounded-md transition-colors p-2 hover:bg-gray-100'
            >
              <div className='flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-oh-lightTeal text-oh-primary'>
                <LogIn size={20} strokeWidth={1.5} />
              </div>
              <span className='text-base font-semibold text-oh-black'>
                Log in to MyOfficeHub
              </span>
            </Link>
          </div>
        </div>
      )}

      <div className={`${NAV_DROPDOWN_PROMO_PANEL_CLASS}${hasPartners ? "" : " border-t-0"}`}>
        <Link
          data-spinner='true'
          to={`${regionPrefix}/add-listing`.replace(/\/+/g, "/")}
          prefetch='intent'
          className='text-xs font-semibold uppercase tracking-[0.08em] text-oh-primary transition-colors hover:text-oh-dullTeal'
        >
          Become a Partner
        </Link>

        <span className='mt-2 text-lg font-bold leading-7 text-black'>
          Rent Out Your Office Space
        </span>

        <img
          src={`https://ik.imagekit.io/officehub/tr:h-600,f-avif/${process.env.PUBLIC_REMIX_APP_ASSESTS_IMAGES}/assets/home-page/partner_header.avif`}
          alt='Modern flexible office space'
          className='mt-4 h-44 w-full rounded-lg object-cover'
          loading='lazy'
        />

        <p className='mt-4 text-sm leading-7 text-oh-dark-gray'>
          Turn your unused office space into revenue by listing it on the{" "}
          <span className='font-semibold text-black'>
            world’s largest marketplace
          </span>{" "}
          for flexible and shared workspaces.
        </p>

        <button
          type='button'
          className='mt-auto flex h-12 w-full shrink-0 items-center justify-center gap-2 rounded-md bg-oh-primary text-sm font-semibold text-white transition-colors hover:bg-oh-dullTeal'
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            onOpenAddListing?.();
          }}
        >
          Add Listing
          <ArrowRight size={18} />
        </button>
      </div>
    </Card>
  );
}
